import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { fetchAllAdminLeagues } from '../actions/leaguesActions';
import { FiX } from 'react-icons/fi';
import SelectSearch from 'react-select-search';
import Fuse from 'fuse.js';
import moment from 'moment';
import axios from 'axios';
import { BASE_URL } from "../constants/config";
import { getTokensFromStore, checkIfTokenExpired } from '../utilities/tokens';
import { refreshTokens } from '../actions/usersActions';
import '../styles/createMatchModal.css';

function CreateMatchModal({ isOpen, onClose, onSuccess, preselectedLeague, preselectedSeason, preselectedStage }) {
  const dispatch = useDispatch();
  
  // State для данных
  const [leagues, setLeagues] = useState([]);
  const [selectedLeague, setSelectedLeague] = useState(null);
  const [seasons, setSeasons] = useState([]);
  const [selectedSeason, setSelectedSeason] = useState(null);
  const [stages, setStages] = useState([]);
  const [selectedStage, setSelectedStage] = useState(null);
  
  const [teams, setTeams] = useState([]);
  const [homeTeam, setHomeTeam] = useState(null);
  const [guestTeam, setGuestTeam] = useState(null);
  const [isHomeTeamModalOpen, setIsHomeTeamModalOpen] = useState(false);
  const [isGuestTeamModalOpen, setIsGuestTeamModalOpen] = useState(false);
  
  // Фильтрованные списки команд
  const homeTeamOptions = teams.filter(team => team.value !== guestTeam);
  const guestTeamOptions = teams.filter(team => team.value !== homeTeam);
  
  const [stadiums, setStadiums] = useState([]);
  const [selectedStadium, setSelectedStadium] = useState(null);
  
  const [referees, setReferees] = useState([]);
  const [selectedReferee, setSelectedReferee] = useState(null);
  
  const [matchDate, setMatchDate] = useState(moment().format('YYYY-MM-DD'));
  const [matchTime, setMatchTime] = useState('');
  const [halfTime, setHalfTime] = useState('30');
  const [extraHalfTime, setExtraHalfTime] = useState('15');
  
  const [extraTimeAvailable, setExtraTimeAvailable] = useState(false);
  const [penaltiesAvailable, setPenaltiesAvailable] = useState(false);
  const [timelineEnabled, setTimelineEnabled] = useState(true);
  
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });
  
  // Проверка дубликатов
  const [duplicateMatches, setDuplicateMatches] = useState([]);
  const [checkingDuplicate, setCheckingDuplicate] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadLeagues();
      loadStadiums();
      loadReferees();
    }
  }, [isOpen]);

  // Восстанавливаем стадион, судью, дату и время тайма после загрузки списков
  useEffect(() => {
    if (isOpen && stadiums.length > 0 && referees.length > 0 && !preselectedLeague && !preselectedSeason && !preselectedStage) {
      const savedMatchData = localStorage.getItem('lastCreatedMatchData');
      if (savedMatchData) {
        try {
          const { stadium, referee, date, halfTime } = JSON.parse(savedMatchData);
          // Устанавливаем значения после загрузки списков
          if (stadium) setSelectedStadium(stadium);
          if (referee) setSelectedReferee(referee);
          if (date) setMatchDate(date);
          if (halfTime) setHalfTime(halfTime);
        } catch (e) {
          console.error('Error parsing saved match data:', e);
        }
      }
    }
  }, [isOpen, stadiums, referees, preselectedLeague, preselectedSeason, preselectedStage]);

  // Предзаполнение данных
  useEffect(() => {
    if (isOpen && leagues.length > 0) {
      // Сначала проверяем preselectedLeague
      let leagueToSet = null;
      
      if (preselectedLeague) {
        leagueToSet = leagues.find(l => l.value === preselectedLeague.value);
      } else {
        // Если нет preselected, пробуем загрузить из localStorage
        const savedMatchData = localStorage.getItem('lastCreatedMatchData');
        if (savedMatchData) {
          try {
            const { league } = JSON.parse(savedMatchData);
            if (league && league.value) {
              leagueToSet = leagues.find(l => l.value === league.value);
            }
          } catch (e) {
            console.error('Error parsing saved match data:', e);
          }
        }
      }
      
      if (leagueToSet) {
        // Устанавливаем лигу
        setSelectedLeague(leagueToSet);
        
        // Загружаем сезоны для лиги
        if (leagueToSet.seasons && leagueToSet.seasons.length > 0) {
          const formattedSeasons = leagueToSet.seasons.map((item, index) => ({
            value: item.season_id,
            name: item.season_name,
            key: index,
            ...item
          }));
          setSeasons(formattedSeasons);
          
          // Устанавливаем сезон
          let seasonToSet = null;
          
          if (preselectedSeason) {
            seasonToSet = formattedSeasons.find(s => s.value === preselectedSeason.value);
          } else {
            // Пробуем загрузить из localStorage
            const savedMatchData = localStorage.getItem('lastCreatedMatchData');
            if (savedMatchData) {
              try {
                const { season } = JSON.parse(savedMatchData);
                if (season && season.value) {
                  seasonToSet = formattedSeasons.find(s => s.value === season.value);
                }
              } catch (e) {
                console.error('Error parsing saved season data:', e);
              }
            }
          }
          
          if (seasonToSet) {
            const season = seasonToSet;
            
            if (season) {
              setSelectedSeason(season);
              
              // Загружаем команды для сезона
              loadTeamsForSeason(leagueToSet.value, season.value).catch(err => {
                console.error('Error loading teams:', err);
              });
              
              // Загружаем стадии для сезона
              loadStagesForSeason(leagueToSet.value, season.value).then(loadedStages => {
                let stageToSet = null;
                
                if (preselectedStage && loadedStages.length > 0) {
                  stageToSet = loadedStages.find(st => st.value === preselectedStage.id);
                } else {
                  // Пробуем загрузить из localStorage
                  const savedMatchData = localStorage.getItem('lastCreatedMatchData');
                  if (savedMatchData && loadedStages.length > 0) {
                    try {
                      const { stage } = JSON.parse(savedMatchData);
                      if (stage && stage.value) {
                        stageToSet = loadedStages.find(st => st.value === stage.value);
                      }
                    } catch (e) {
                      console.error('Error parsing saved stage data:', e);
                    }
                  }
                }
                
                if (stageToSet) {
                  setSelectedStage(stageToSet.value); // Только ID, не весь объект!
                }
              }).catch(err => {
                console.error('Error loading stages:', err);
              });
            }
          }
        }
      }
    }
  }, [isOpen, preselectedLeague, preselectedSeason, preselectedStage, leagues]);

  useEffect(() => {
    // Проверяем дубликаты когда выбраны обе команды, лига и сезон
    if (homeTeam && guestTeam && selectedLeague && selectedSeason) {
      checkForDuplicateMatch();
    } else {
      setDuplicateMatches([]);
    }
  }, [homeTeam, guestTeam, selectedLeague, selectedSeason]);

  const getDuplicateMatchTour = (match) => {
    return match?.tour_name || match?.tour || match?.round_name || match?.round || match?.tour_number || match?.short_stage_name || null;
  };

  const checkForDuplicateMatch = async () => {
    try {
      setCheckingDuplicate(true);
      const isTokenExpired = checkIfTokenExpired();
      if (isTokenExpired) {
        await dispatch(refreshTokens());
      }
      const tokens = getTokensFromStore();
      
      // Проверяем оба варианта: home-guest и guest-home
      const response = await axios.post(
        BASE_URL.url + '/matches/checkMatchExists',
        {
          league_id: selectedLeague.value,
          season_id: selectedSeason.value,
          team1_id: homeTeam,
          team2_id: guestTeam
        },
        {
          headers: {
            Authorization: "Bearer " + tokens.access_token,
            Accept: 'application/json',
            'Content-Type': 'application/json',
          }
        }
      );
      
      // Поддержка API с одним матчем и API со списком матчей
      if (response.data && response.data.exists) {
        const duplicatesFromApi = Array.isArray(response.data.matches)
          ? response.data.matches
          : response.data.match
            ? [response.data.match]
            : [];
        setDuplicateMatches(duplicatesFromApi);
      } else {
        setDuplicateMatches([]);
      }
    } catch (error) {
      // Если API не существует, делаем ручную проверку
      if (error.response && error.response.status === 404) {
        await manualCheckDuplicateMatch();
      } else {
        console.error('Error checking duplicate match:', error);
        setDuplicateMatches([]);
      }
    } finally {
      setCheckingDuplicate(false);
    }
  };

  const manualCheckDuplicateMatch = async () => {
    try {
      const isTokenExpired = checkIfTokenExpired();
      if (isTokenExpired) {
        await dispatch(refreshTokens());
      }
      const tokens = getTokensFromStore();
      
      // Загружаем матчи лиги для проверки
      const response = await axios.post(
        BASE_URL.url + `/leagues/${selectedLeague.value}/matches`,
        { 
          season_id: selectedSeason.value 
        },
        {
          headers: {
            Authorization: "Bearer " + tokens.access_token,
            Accept: 'application/json',
            'Content-Type': 'application/json',
          }
        }
      );
      
      const matchesData = Array.isArray(response.data) ? response.data : response.data.data;
      
      if (matchesData && matchesData.length > 0) {
        // Ищем все матчи с такими же командами (в любом порядке)
        const duplicates = matchesData.filter(match => 
          (match.home_team_id === homeTeam && match.guest_team_id === guestTeam) ||
          (match.home_team_id === guestTeam && match.guest_team_id === homeTeam)
        );
        
        setDuplicateMatches(duplicates);
      } else {
        setDuplicateMatches([]);
      }
    } catch (error) {
      console.error('Error manual checking duplicate match:', error);
      setDuplicateMatches([]);
    }
  };

  const loadLeagues = async () => {
    try {
      const isTokenExpired = checkIfTokenExpired();
      if (isTokenExpired) {
        await dispatch(refreshTokens());
      }
      const tokens = getTokensFromStore();
      
      const response = await axios.post(
        BASE_URL.url + '/leagues/1/adminLeagues',
        {},
        {
          headers: {
            Authorization: "Bearer " + tokens.access_token,
            Accept: 'application/json',
            'Content-Type': 'application/json',
          }
        }
      );
      
      const leaguesData = Array.isArray(response.data) ? response.data : response.data.data;
      
      if (leaguesData && leaguesData.length > 0) {
        const formattedLeagues = leaguesData
          .filter(l => l.league_id > 0)
          .map((item, index) => ({
            value: item.league_id,
            name: `${item.league_name} (${item.championat_name})`,
            key: index,
            seasons: item.seasons || [],
            ...item
          }));
        
        setLeagues(formattedLeagues);
      }
    } catch (error) {
      console.error('Error loading leagues:', error);
    }
  };

  const loadStadiums = async () => {
    try {
      const isTokenExpired = checkIfTokenExpired();
      if (isTokenExpired) {
        await dispatch(refreshTokens());
      }
      const tokens = getTokensFromStore();
      
      const response = await axios.post(
        BASE_URL.url + '/stadiums/getAllStadiums',
        {},
        {
          headers: {
            Authorization: "Bearer " + tokens.access_token,
            Accept: 'application/json',
            'Content-Type': 'application/json',
          }
        }
      );
      
      const stadiumsData = Array.isArray(response.data) ? response.data : response.data.data;
      
      if (stadiumsData && stadiumsData.length > 0) {
        const formattedStadiums = stadiumsData.map((item, index) => ({
          ...item,
          value: item.id,
          name: item.name,
          key: index
        }));
        setStadiums(formattedStadiums);
      }
    } catch (error) {
      console.error('Error loading stadiums:', error);
    }
  };

  const loadReferees = async () => {
    try {
      const isTokenExpired = checkIfTokenExpired();
      if (isTokenExpired) {
        await dispatch(refreshTokens());
      }
      const tokens = getTokensFromStore();
      
      const response = await axios.post(
        BASE_URL.url + '/judges/getAllReferees',
        {},
        {
          headers: {
            Authorization: "Bearer " + tokens.access_token,
            Accept: 'application/json',
            'Content-Type': 'application/json',
          }
        }
      );
      
      const refereesData = Array.isArray(response.data) ? response.data : response.data.data;
      
      if (refereesData && refereesData.length > 0) {
        const formattedReferees = refereesData.map((item, index) => {
          const familyName = item.family ? item.family.trim() : '';
          const firstName = item.name ? item.name.trim() : '';
          const fullName = familyName ? `${firstName} ${familyName}` : firstName;
          return {
            ...item,
            value: item.id,
            name: fullName,
            key: index
          };
        });
        setReferees(formattedReferees);
      }
    } catch (error) {
      console.error('Error loading referees:', error);
    }
  };

  const handleLeagueChange = async (league_id) => {
    const league = leagues.find(l => l.value === league_id);
    
    setSelectedLeague(league);
    setSelectedSeason(null);
    setSelectedStage(null);
    setHomeTeam(null);
    setGuestTeam(null);
    setStages([]);
    setTeams([]);
    setDuplicateMatches([]);
    
    if (!league || league_id <= 0) {
      setSeasons([]);
      return;
    }
    
    if (league.seasons && league.seasons.length > 0) {
      const formattedSeasons = league.seasons.map((item, index) => ({
        value: item.season_id,
        name: item.season_name,
        key: index,
        ...item
      }));
      setSeasons(formattedSeasons);
    } else {
      setSeasons([]);
    }
  };

  const loadStagesForSeason = async (league_id, season_id) => {
    try {
      const isTokenExpired = checkIfTokenExpired();
      if (isTokenExpired) {
        await dispatch(refreshTokens());
      }
      const tokens = getTokensFromStore();
      
      const stagesResponse = await axios.post(
        BASE_URL.url + `/leagues/${league_id}/getLeagueStages`,
        { 
          league_id: league_id,
          season_id: season_id 
        },
        {
          headers: {
            Authorization: "Bearer " + tokens.access_token,
            Accept: 'application/json',
            'Content-Type': 'application/json',
          }
        }
      );
      
      const stagesData = Array.isArray(stagesResponse.data) ? stagesResponse.data : stagesResponse.data.data;
      
      if (stagesData && stagesData.length > 0) {
        const formattedStages = stagesData.map((item, index) => ({
          ...item,
          value: item.id,
          name: item.name,
          key: index
        }));
        setStages(formattedStages);
        return formattedStages;
      }
      return [];
    } catch (error) {
      console.error('Error loading stages:', error);
      setStages([]);
      return [];
    }
  };

  const loadTeamsForSeason = async (league_id, season_id) => {
    try {
      const isTokenExpired = checkIfTokenExpired();
      if (isTokenExpired) {
        await dispatch(refreshTokens());
      }
      const tokens = getTokensFromStore();
      
      const teamsResponse = await axios.post(
        BASE_URL.url + `/leagues/${league_id}/teams`,
        { season_id: season_id },
        {
          headers: {
            Authorization: "Bearer " + tokens.access_token,
            Accept: 'application/json',
            'Content-Type': 'application/json',
          }
        }
      );
      
      const teamsData = Array.isArray(teamsResponse.data) ? teamsResponse.data : teamsResponse.data.data;
      
      if (teamsData && teamsData.length > 0) {
        const formattedTeams = teamsData.map((item, index) => {
          const logoPath = item.image ? BASE_URL.url + item.image : `${BASE_URL.url}/images/teams/${item.team_id}.png`;
          return {
            ...item,
            value: item.team_id,
            name: item.name,
            logo: logoPath,
            key: index
          };
        });
        setTeams(formattedTeams);
        return formattedTeams;
      }
      return [];
    } catch (error) {
      console.error('Error loading teams:', error);
      setTeams([]);
      return [];
    }
  };

  const handleSeasonChange = async (season_id) => {
    const season = seasons.find(s => s.value === season_id);
    setSelectedSeason(season);
    setSelectedStage(null);
    setStages([]);
    setTeams([]);
    setHomeTeam(null);
    setGuestTeam(null);
    setDuplicateMatches([]);
    
    if (!season || !season_id || !selectedLeague) return;
    
    // Загружаем команды и стадии
    await loadTeamsForSeason(selectedLeague.value, season_id);
    await loadStagesForSeason(selectedLeague.value, season_id);
  };

  const fuzzySearch = (options) => {
    const fuse = new Fuse(options, {
      keys: ['name'],
      threshold: 0.3,
    });

    return (value) => {
      if (!value.length) {
        return options;
      }
      return fuse.search(value).map(item => item.item);
    };
  };

  const validateForm = () => {
    if (!selectedLeague) {
      setMessage({ text: 'Выберите лигу', type: 'error' });
      return false;
    }
    if (!selectedSeason) {
      setMessage({ text: 'Выберите сезон', type: 'error' });
      return false;
    }
    if (!selectedStage) {
      setMessage({ text: 'Выберите стадию', type: 'error' });
      return false;
    }
    if (!homeTeam) {
      setMessage({ text: 'Выберите домашнюю команду', type: 'error' });
      return false;
    }
    if (!guestTeam) {
      setMessage({ text: 'Выберите гостевую команду', type: 'error' });
      return false;
    }
    if (homeTeam === guestTeam) {
      setMessage({ text: 'Команды должны быть разными', type: 'error' });
      return false;
    }
    if (!selectedStadium) {
      setMessage({ text: 'Выберите стадион', type: 'error' });
      return false;
    }
    if (!matchDate) {
      setMessage({ text: 'Укажите дату матча', type: 'error' });
      return false;
    }
    if (!selectedReferee) {
      setMessage({ text: 'Выберите судью', type: 'error' });
      return false;
    }
    // Пустое поле уходило как NaN (в JSON — null)
    const half = Number(halfTime);
    if (!Number.isInteger(half) || half < 1 || half > 90) {
      setMessage({ text: 'Время тайма — целое число минут от 1 до 90', type: 'error' });
      return false;
    }
    if (extraTimeAvailable && extraHalfTime !== '' && extraHalfTime !== null && extraHalfTime !== undefined) {
      const extra = Number(extraHalfTime);
      if (!Number.isInteger(extra) || extra < 1 || extra > 60) {
        setMessage({ text: 'Доп. тайм — целое число минут от 1 до 60', type: 'error' });
        return false;
      }
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setMessage({ text: '', type: '' });

    try {
      const isTokenExpired = checkIfTokenExpired();
      if (isTokenExpired) {
        await dispatch(refreshTokens());
      }
      const tokens = getTokensFromStore();

      const matchData = {
        home_team_id: homeTeam,
        guest_team_id: guestTeam,
        league_id: selectedLeague.value,
        stadium_id: selectedStadium,
        date: matchDate,
        time: matchTime || null,
        judje_id: selectedReferee,
        season_id: selectedSeason.value,
        stage_id: selectedStage,
        half_time: parseInt(halfTime),
        extra_time_available: extraTimeAvailable ? 1 : null,
        penalty_kicks_available: penaltiesAvailable ? 1 : null,
        timeline_disabled: !timelineEnabled,
        extra_half_time: extraTimeAvailable && extraHalfTime ? parseInt(extraHalfTime) : null,
      };

      const response = await axios.post(
        BASE_URL.url + '/matches/setMatch',
        { match_data: matchData },
        {
          headers: {
            Authorization: "Bearer " + tokens.access_token,
            Accept: 'application/json',
            'Content-Type': 'application/json',
          }
        }
      );

      if (response.data) {
        // Сохраняем выбранные значения в localStorage
        const matchDataToSave = {
          league: {
            value: selectedLeague.value,
            name: selectedLeague.name
          },
          season: {
            value: selectedSeason.value,
            name: selectedSeason.name
          },
          stage: {
            value: selectedStage,
            name: stages.find(s => s.value === selectedStage)?.name || ''
          },
          stadium: selectedStadium,
          referee: selectedReferee,
          date: matchDate,
          halfTime: halfTime
        };
        localStorage.setItem('lastCreatedMatchData', JSON.stringify(matchDataToSave));
        
        setMessage({ text: 'Матч успешно создан!', type: 'success' });
        setTimeout(() => {
          resetForm();
          if (onSuccess) {
            onSuccess();
          }
          onClose();
        }, 1500);
      }
    } catch (error) {
      console.error('Error creating match:', error);
      setMessage({ 
        text: error.response?.data?.message || 'Ошибка при создании матча', 
        type: 'error' 
      });
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setSelectedLeague(null);
    setSelectedSeason(null);
    setSelectedStage(null);
    setHomeTeam(null);
    setGuestTeam(null);
    setSelectedStadium(null);
    setSelectedReferee(null);
    setMatchDate(moment().format('YYYY-MM-DD'));
    setMatchTime('');
    setHalfTime('30');
    setExtraHalfTime('15');
    setExtraTimeAvailable(false);
    setPenaltiesAvailable(false);
    setTimelineEnabled(true);
    setSeasons([]);
    setStages([]);
    setTeams([]);
    setDuplicateMatches([]);
    setMessage({ text: '', type: '' });
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content create-match-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Создание матча</h2>
          <button className="modal-close" onClick={onClose}>
            <FiX size={24} />
          </button>
        </div>
      
        <div className="modal-body">
          {message.text && (
            <div className={`message-box ${message.type === 'success' ? 'message-success' : 'message-error'}`}>
              {message.text}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="form-grid-modal">
              {/* Левая колонка */}
              <div className="form-column-modal">
                <div className="form-section-modal">
                  <label className="form-label-modal form-label-required">
                    Лига
                  </label>
                  <SelectSearch
                    options={leagues}
                    search
                    value={selectedLeague?.value}
                    onChange={handleLeagueChange}
                    filterOptions={fuzzySearch}
                    emptyMessage="Лига не найдена"
                    placeholder="Выберите лигу"
                  />
                </div>

                {selectedLeague && seasons.length > 0 && (
                  <div className="form-section-modal">
                    <label className="form-label-modal form-label-required">
                      Сезон
                    </label>
                    <SelectSearch
                      options={seasons}
                      search
                      value={selectedSeason?.value}
                      onChange={handleSeasonChange}
                      filterOptions={fuzzySearch}
                      emptyMessage="Сезон не найден"
                      placeholder="Выберите сезон"
                    />
                  </div>
                )}

                {selectedSeason && (
                  <div className="form-section-modal">
                    <label className="form-label-modal form-label-required">
                      Стадия
                    </label>
                    <SelectSearch
                      options={stages}
                      search
                      value={selectedStage}
                      onChange={setSelectedStage}
                      filterOptions={fuzzySearch}
                      emptyMessage={stages.length === 0 ? "Нет доступных стадий для этого сезона" : "Стадия не найдена"}
                      placeholder="Выберите стадию"
                    />
                  </div>
                )}

                {selectedSeason && teams.length > 0 && (
                  <>
                    <div className="form-section-modal">
                      <label className="form-label-modal form-label-required">
                        Домашняя команда
                      </label>
                      <div 
                        className="team-selector-modal"
                        onClick={() => setIsHomeTeamModalOpen(true)}
                      >
                        {homeTeam ? (
                          <div className="selected-team-modal">
                            <img 
                              src={teams.find(t => t.value === homeTeam)?.logo} 
                              alt="Team logo"
                              className="team-logo-modal"
                            />
                            <span className="team-name-modal">{teams.find(t => t.value === homeTeam)?.name}</span>
                          </div>
                        ) : (
                          <div className="team-placeholder-modal">
                            <span>Нажмите для выбора команды</span>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="form-section-modal">
                      <label className="form-label-modal form-label-required">
                        Гостевая команда
                      </label>
                      <div 
                        className="team-selector-modal"
                        onClick={() => setIsGuestTeamModalOpen(true)}
                      >
                        {guestTeam ? (
                          <div className="selected-team-modal">
                            <img 
                              src={teams.find(t => t.value === guestTeam)?.logo} 
                              alt="Team logo"
                              className="team-logo-modal"
                            />
                            <span className="team-name-modal">{teams.find(t => t.value === guestTeam)?.name}</span>
                          </div>
                        ) : (
                          <div className="team-placeholder-modal">
                            <span>Нажмите для выбора команды</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </>
                )}

                {/* Предупреждение о дубликате */}
                {checkingDuplicate && (
                  <div className="duplicate-check-loading">
                    Проверка на дубликаты...
                  </div>
                )}
                
                {duplicateMatches.length > 0 && !checkingDuplicate && (
                  <div className="duplicate-warning">
                    <div className="duplicate-warning-header">
                      ⚠️ Найдено дублей: {duplicateMatches.length}
                    </div>
                    {duplicateMatches.map((match, index) => (
                      <div
                        key={match.id || `${match.home_team_id}-${match.guest_team_id}-${match.date || index}`}
                        className="duplicate-match-info"
                      >
                        <div className="duplicate-match-teams">
                          <span>{match.home_team_name || teams.find(t => t.value === match.home_team_id)?.name}</span>
                          <span> vs </span>
                          <span>{match.guest_team_name || teams.find(t => t.value === match.guest_team_id)?.name}</span>
                        </div>
                        {getDuplicateMatchTour(match) && (
                          <div className="duplicate-match-tour">
                            Тур: {getDuplicateMatchTour(match)}
                          </div>
                        )}
                        {match.date && (
                          <div className="duplicate-match-date">
                            Дата: {moment(match.date).format('DD.MM.YYYY')} 
                            {match.time && ` в ${match.time}`}
                          </div>
                        )}
                        {match.stadium_name && (
                          <div className="duplicate-match-stadium">
                            Стадион: {match.stadium_name}
                          </div>
                        )}
                      </div>
                    ))}
                    <div className="duplicate-warning-note">
                      Вы все равно можете создать этот матч
                    </div>
                  </div>
                )}
              </div>

              {/* Правая колонка */}
              <div className="form-column-modal">
                <div className="form-section-modal">
                  <label className="form-label-modal form-label-required">
                    Стадион
                  </label>
                  <SelectSearch
                    options={stadiums}
                    search
                    value={selectedStadium}
                    onChange={setSelectedStadium}
                    filterOptions={fuzzySearch}
                    emptyMessage="Стадион не найден"
                    placeholder="Выберите стадион"
                  />
                </div>

                <div className="form-section-modal">
                  <label className="form-label-modal form-label-required">
                    Судья
                  </label>
                  <SelectSearch
                    options={referees}
                    search
                    value={selectedReferee}
                    onChange={setSelectedReferee}
                    filterOptions={fuzzySearch}
                    emptyMessage="Судья не найден"
                    placeholder="Выберите судью"
                  />
                </div>

                <div className="date-time-row-modal">
                  <div className="form-section-modal">
                    <label className="form-label-modal form-label-required">
                      Дата
                    </label>
                    <input
                      className="input-field-modal"
                      type="date"
                      value={matchDate}
                      onChange={(e) => setMatchDate(e.target.value)}
                    />
                  </div>

                  <div className="form-section-modal">
                    <label className="form-label-modal">
                      Время
                    </label>
                    <input
                      className="input-field-modal"
                      type="time"
                      value={matchTime}
                      onChange={(e) => setMatchTime(e.target.value)}
                    />
                  </div>
                </div>

                <div className="form-section-modal">
                  <label className="form-label-modal">
                    Время тайма (минуты)
                  </label>
                  <input
                    className="input-field-modal"
                    type="number"
                    value={halfTime}
                    onChange={(e) => setHalfTime(e.target.value)}
                    min="1"
                  />
                </div>

                <div className="form-section-modal">
                  <label className="checkbox-item-modal">
                    <input
                      type="checkbox"
                      checked={extraTimeAvailable}
                      onChange={(e) => setExtraTimeAvailable(e.target.checked)}
                    />
                    <span>Возможны дополнительные таймы</span>
                  </label>
                </div>

                {extraTimeAvailable && (
                  <div className="form-section-modal" style={{ marginLeft: '24px' }}>
                    <label className="form-label-modal">
                      Время доп. тайма (минуты)
                    </label>
                    <input
                      className="input-field-modal"
                      type="number"
                      value={extraHalfTime}
                      onChange={(e) => setExtraHalfTime(e.target.value)}
                      min="1"
                    />
                  </div>
                )}

                <div className="form-section-modal">
                  <label className="checkbox-item-modal">
                    <input
                      type="checkbox"
                      checked={penaltiesAvailable}
                      onChange={(e) => setPenaltiesAvailable(e.target.checked)}
                    />
                    <span>Возможны пенальти</span>
                  </label>
                </div>

                <div className="form-section-modal">
                  <label className="checkbox-item-modal">
                    <input
                      type="checkbox"
                      checked={timelineEnabled}
                      onChange={(e) => setTimelineEnabled(e.target.checked)}
                    />
                    <span>Включить таймлайн</span>
                  </label>
                </div>
              </div>
            </div>

            <div className="button-group-modal">
              <button
                type="submit"
                disabled={loading}
                className="btn-modal btn-primary-modal"
              >
                {loading ? 'Создание...' : 'Создать матч'}
              </button>

              <button
                type="button"
                onClick={resetForm}
                disabled={loading}
                className="btn-modal btn-secondary-modal"
              >
                Очистить
              </button>

              <button
                type="button"
                onClick={onClose}
                disabled={loading}
                className="btn-modal btn-secondary-modal"
              >
                Отмена
              </button>
            </div>
          </form>

          {/* Модальное окно выбора домашней команды */}
          {isHomeTeamModalOpen && (
            <div className="team-modal-overlay" onClick={() => setIsHomeTeamModalOpen(false)}>
              <div className="team-modal" onClick={(e) => e.stopPropagation()}>
                <div className="team-modal-header">
                  <h3>Выберите домашнюю команду</h3>
                  <button className="team-modal-close" onClick={() => setIsHomeTeamModalOpen(false)}>×</button>
                </div>
                <div className="team-modal-body">
                  {homeTeamOptions.map((team) => (
                    <div
                      key={team.value}
                      className={`team-card ${homeTeam === team.value ? 'selected' : ''}`}
                      onClick={() => {
                        setHomeTeam(team.value);
                        setIsHomeTeamModalOpen(false);
                      }}
                    >
                      <img 
                        src={team.logo} 
                        alt={team.name}
                        className="team-card-logo"
                      />
                      <span className="team-card-name">{team.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Модальное окно выбора гостевой команды */}
          {isGuestTeamModalOpen && (
            <div className="team-modal-overlay" onClick={() => setIsGuestTeamModalOpen(false)}>
              <div className="team-modal" onClick={(e) => e.stopPropagation()}>
                <div className="team-modal-header">
                  <h3>Выберите гостевую команду</h3>
                  <button className="team-modal-close" onClick={() => setIsGuestTeamModalOpen(false)}>×</button>
                </div>
                <div className="team-modal-body">
                  {guestTeamOptions.map((team) => (
                    <div
                      key={team.value}
                      className={`team-card ${guestTeam === team.value ? 'selected' : ''}`}
                      onClick={() => {
                        setGuestTeam(team.value);
                        setIsGuestTeamModalOpen(false);
                      }}
                    >
                      <img 
                        src={team.logo} 
                        alt={team.name}
                        className="team-card-logo"
                      />
                      <span className="team-card-name">{team.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default CreateMatchModal;
